import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { ProfileContext } from "../context/ProfileContext";
import { SchedJobContext } from "../context/JobContextScheduler";
import { db } from "../firebase";
import { collection, doc, getDocs, query, where, orderBy, getDoc } from "firebase/firestore";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { HEADERTEXT_TYPES, FB_COLLECTION_PATH } from "../functions/postActionTypes";
import {
  IonContent,
  IonPage,
  IonGrid,
  IonButton,
  IonLabel,
  IonItem,
  IonList,
  IonCheckbox,
  IonAvatar,
  IonSearchbar,
  IonFooter,
  IonToolbar,
  useIonAlert,
  IonBadge,
  IonAccordionGroup,
  IonAccordion,
  IonText,
  useIonToast,
} from "@ionic/react";
import "../theme/AppStyles.css";
import { WerkerSchedulerHeader } from "../components/WerkHeader";
import { fx_FB_WerkerInvitePending, WerkerChange } from "../functions/fx_FB_WerkerInvitePending";
import { CrewContext } from "../context/CrewContext";
import { useWerkerSelection, AssociatesData } from "../hooks/scheduler/useWerkerSelection";
import { filterOutSupportMonkey, SUPPORT_MONKEY_UID } from "../constants/supportConstants";
import { formatForJobInvitation } from "../utils/userInfoFormatting";

interface SelectWerkersSeriesProps extends RouteComponentProps<{ seriesId: string }> {}

interface SelectWerkersSeriesDesktopMode {
  isDesktopMode: boolean;
  seriesId: string;
  jobIds?: string[];
  onComplete: (result?: { nextView?: "details"; jobId?: string }) => void;
}

interface SelectWerkersSeriesPageProps extends Partial<SelectWerkersSeriesProps> {
  desktopMode?: SelectWerkersSeriesDesktopMode;
}

const SelectWerkersSeries: React.FC<SelectWerkersSeriesPageProps> = ({ match, desktopMode }) => {
  const history = useHistory<any>();
  const { profile, myAssociates } = useContext(ProfileContext);
  const { schedJob } = useContext(SchedJobContext);
  const { crews, crewsLoading } = useContext(CrewContext);
  const [presentAlert] = useIonAlert();
  const [presentToast] = useIonToast();

  const [resolvedJobIds, setResolvedJobIds] = useState<string[]>([]);

  const isDesktopMode = desktopMode?.isDesktopMode === true;
  const seriesIdFromRoute = desktopMode?.seriesId ?? match?.params?.seriesId;
  const historyState = history?.location?.state as { jobIds?: string[]; jobDocIds?: string[] } | undefined;

  // This page expects jobIds in history state
  const jobIds: string[] = useMemo(
    () =>
      (desktopMode?.jobIds && desktopMode.jobIds.length > 0 ? desktopMode.jobIds : undefined) ||
      historyState?.jobIds ||
      historyState?.jobDocIds ||
      [],
    [desktopMode?.jobIds, historyState?.jobDocIds, historyState?.jobIds]
  );

  // Use werker selection hook for all selection logic
  const {
    searchText,
    setSearchText,
    crewAccordionValue,
    setCrewAccordionValue,
    initialAssociatesCrew,
    setInitialAssociatesCrew,
    associatesCrew,
    setAssociatesCrew,
    contentRef,
    onContentScroll,
    filteredSorted,
    allFilteredSelected,
    someFilteredSelected,
    visibleUidsCount,
    visibleSelectedCount,
    handleSelectAllClick,
    handleCrewList,
    applyCrewToSelection,
    getInitials,
  } = useWerkerSelection();

  const getJobContextInfo = useCallback(
    async (jobId: string): Promise<any | null> => {
      // 1) prefer scheduler context if already loaded
      const fromContext = schedJob?.find?.((x: any) => x?.JobDocId === jobId);
      if (fromContext) return fromContext;

      try {
        // 2) fastest + no composite index required: direct doc read
        const directRef = doc(db, FB_COLLECTION_PATH.JobsDocs, jobId);
        const directSnap = await getDoc(directRef);

        if (directSnap.exists()) {
          const data = directSnap.data() as any;
          return {
            JobDocId: data?.jobInfo?.core?.uniqueJobId || jobId,
            ShiftIdentifier: data?.jobInfo?.core?.shiftIdentifier || "",
            Company: data?.jobInfo?.core?.company || "",
            StartDateTimeRaw: data?.jobInfo?.core?.dateTimeStart || "",
            FinishDateTimeRaw: data?.jobInfo?.core?.dateTimeFinish || "",
            Location: data?.jobInfo?.core?.location || "",
            LocationCoordinates: data?.jobInfo?.core?.locationCoordinates || "",
            Pay: data?.jobInfo?.core?.pay || "",
            ShiftNotes: data?.jobInfo?.core?.shiftNotes || "",
            JobStatusSched: data?.jobInfo?.status?.jobStatusSched || "",
            scheduledCount: data?.jobInfo?.status?.scheduledCount || 0,
            numJobSlots: data?.jobInfo?.core?.numJobSlots ?? 1,
            seriesId: data?.jobInfo?.core?.seriesId ?? null,
            seriesIndex: data?.jobInfo?.core?.seriesIndex ?? null,
            seriesTotal: data?.jobInfo?.core?.seriesTotal ?? null,
          };
        }

        // 3) fallback if jobId is NOT the doc id (older data shape)
        const q = query(collection(db, FB_COLLECTION_PATH.JobsDocs), where("jobInfo.core.uniqueJobId", "==", jobId));
        const snap = await getDocs(q);
        if (snap.empty) return null;

        const d = snap.docs[0];
        const data = d.data() as any;

        return {
          JobDocId: data?.jobInfo?.core?.uniqueJobId || jobId,
          ShiftIdentifier: data?.jobInfo?.core?.shiftIdentifier || "",
          Company: data?.jobInfo?.core?.company || "",
          StartDateTimeRaw: data?.jobInfo?.core?.dateTimeStart || "",
          FinishDateTimeRaw: data?.jobInfo?.core?.dateTimeFinish || "",
          Location: data?.jobInfo?.core?.location || "",
          LocationCoordinates: data?.jobInfo?.core?.locationCoordinates || "",
          Pay: data?.jobInfo?.core?.pay || "",
          ShiftNotes: data?.jobInfo?.core?.shiftNotes || "",
          JobStatusSched: data?.jobInfo?.status?.jobStatusSched || "",
          scheduledCount: data?.jobInfo?.status?.scheduledCount || 0,
          numJobSlots: data?.jobInfo?.core?.numJobSlots ?? 1,
          seriesId: data?.jobInfo?.core?.seriesId ?? null,
          seriesIndex: data?.jobInfo?.core?.seriesIndex ?? null,
          seriesTotal: data?.jobInfo?.core?.seriesTotal ?? null,
        };
      } catch (e) {
        // console.log("getJobContextInfo error", e);
        return null;
      }
    },
    [schedJob]
  );

  // Build associates list + preload current invited state from jobFeed (anchor job)
  useEffect(() => {
    const init = async () => {
      // ✅ Resolve jobIds: prefer navigation state, otherwise rebuild from firestore
      let nextResolvedJobIds: string[] = Array.isArray(jobIds) ? jobIds : [];

      if (nextResolvedJobIds.length === 0) {
        try {
          const q = query(
            collection(db, FB_COLLECTION_PATH.JobsDocs),
            where("jobInfo.core.seriesId", "==", seriesIdFromRoute),
            where("docStamp.userInfoFrom.uid", "==", profile.UserId),
            where("docStamp.userInfoTo.uid", "==", profile.UserId),
            orderBy("jobInfo.core.seriesIndex", "asc")
          );

          const snap = await getDocs(q);
          nextResolvedJobIds = snap.docs
            .map((d) => (d.data() as any)?.jobInfo?.core?.uniqueJobId || d.id)
            .filter((v) => typeof v === "string" && v.length > 0);
        } catch (e) {
          // console.log("Failed to rebuild jobIds from seriesId", e);
        }
      }

      setResolvedJobIds(nextResolvedJobIds);

      // Defensive: if we still have no jobs, just show associates uninvited
      const anchorJobId = nextResolvedJobIds?.[0];

      // Build base list from associates
      const baseArray: AssociatesData[] = [];
      myAssociates.forEach((myAss: any) => {
        if (!myAss?.WerkerUid) return;

        baseArray.push(
          formatForJobInvitation(
            {
              WerkerUid: myAss.WerkerUid,
              WerkerFirstName: myAss.WerkerFirstName,
              WerkerLastName: myAss.WerkerLastName,
              WerkerCompany: myAss.WerkerCompany,
              WerkerProfilePicURL: myAss.WerkerProfilePicURL,
              WerkerBio: myAss.WerkerBio,
            },
            false,
            ""
          )
        );
      });

      const cleanedBase = filterOutSupportMonkey(baseArray.filter((v) => !!v.WerkerUid));

      // If we don’t have an anchor job, we can’t preload; keep all false
      if (!anchorJobId) {
        setAssociatesCrew(cleanedBase);
        setInitialAssociatesCrew(cleanedBase);
        return;
      }

      // ✅ Preload invited state from users/{uid}/jobFeed/{anchorJobId}
      // Chunk reads to avoid nuking device
      const MAX_PARALLEL = 60;

      const invitedMap = new Map<string, { invited: boolean; docId: string }>();

      for (let i = 0; i < cleanedBase.length; i += MAX_PARALLEL) {
        const chunk = cleanedBase.slice(i, i + MAX_PARALLEL);

        const snaps = await Promise.all(
          chunk.map((u: AssociatesData) => getDoc(doc(db, FB_COLLECTION_PATH.Users, u.WerkerUid, FB_COLLECTION_PATH.JobFeed, anchorJobId)))
        );

        snaps.forEach((snap: any, idx: number) => {
          const u = chunk[idx];
          if (!u?.WerkerUid) return;

          if (snap.exists()) {
            const data: any = snap.data();
            invitedMap.set(u.WerkerUid, {
              invited: true,
              docId: data?.werkerJobDocId || "",
            });
          } else {
            invitedMap.set(u.WerkerUid, {
              invited: false,
              docId: "",
            });
          }
        });
      }

      const merged = cleanedBase.map((u: AssociatesData) => {
        const hit = invitedMap.get(u.WerkerUid);
        if (!hit) return u;
        return { ...u, isInvited: hit.invited, DocId: hit.docId };
      });

      setAssociatesCrew(merged);
      setInitialAssociatesCrew(merged);
    };

    init();
  }, [
    profile,
    myAssociates,
    seriesIdFromRoute,
    jobIds,
    setAssociatesCrew,
    setInitialAssociatesCrew,
  ]);

  // =========================
  // Submit (Series)
  // =========================
  const handleSubmit = async () => {
    // defensive guardrails ////////////////////////////
    if (!resolvedJobIds || resolvedJobIds.length === 0) {
      await presentAlert({
        header: "Nothing to schedule",
        message: "This series could not be resolved. Please try again.",
        buttons: ["OK"],
      });
      return;
    }

    // defensive guardrails: subscription check //
    if (profile.SubscriptionType !== "Scheduler") {
      history.push({
        pathname: `/Paywall`,
        state: { cameFrom: "SelectWerkersSeries" },
      });
      return;
    }

    // Create docs in CHUNKED batches (series can exceed 500 writes quickly)
    if (!Array.isArray(resolvedJobIds) || resolvedJobIds.length === 0) {
      await presentAlert({
        header: "Missing Jobs",
        message: "No jobs were provided for this series selection.",
        buttons: ["OK"],
      });
      return;
    }

    const selected = associatesCrew.filter((a) => a.isInvited === true);
    const selectedUIDs = selected.map((s) => s.WerkerUid).filter((uid) => !!uid && uid !== SUPPORT_MONKEY_UID);

    // defensive guardrails: no selection /////////////
    if (selectedUIDs.length === 0) {
      await presentAlert({
        header: "No Werkers Selected",
        message: "Please select at least one werker to schedule on this series.",
        buttons: ["OK"],
      });
      return;
    }

    // Alert! confirm applying across N jobs //////////////
    const ok = await new Promise<boolean>((resolve) => {
      presentAlert({
        header: "Apply to Series?",
        message: `This will invite ${selectedUIDs.length} werker(s) on ${resolvedJobIds.length} job(s). Continue?`,
        buttons: [
          { text: "Cancel", role: "cancel", handler: () => resolve(false) },
          { text: "Continue", handler: () => resolve(true) },
        ],
      });
    });

    if (!ok) return;

    // Calculate changed werkers
    const changed = associatesCrew.filter((u) => {
      const init = initialAssociatesCrew.find((x) => x.WerkerUid === u.WerkerUid);
      return init && init.isInvited !== u.isInvited;
    });

    if (changed.length === 0) {
      presentToast({
        message: "No changes to apply.",
        duration: 1200,
        position: "top",
        color: "medium",
      });
      return;
    }

    try {
      // Get job context for all jobs in the series in parallel
      const allJobContexts = await Promise.all(resolvedJobIds.map((id) => getJobContextInfo(id)));
      const firstJobContext = allJobContexts[0];
      if (!firstJobContext) {
        await presentAlert({
          header: "Missing Job Info",
          message: `Could not load job context for jobId: ${resolvedJobIds[0]}.`,
          buttons: ["OK"],
        });
        return;
      }

      // Determine which jobs in the series are not eligible for new invites
      // (already filled or in the past). The cloud function enforces this
      // authoritatively; this client-side pass exists only to surface a
      // user-facing notice and to short-circuit when nothing would invite.
      const nowMs = Date.now();
      const skippedJobIds: string[] = [];
      resolvedJobIds.forEach((id, i) => {
        const ctx = allJobContexts[i];
        if (!ctx) {
          skippedJobIds.push(id);
          return;
        }
        const slots = Number(ctx.numJobSlots ?? 0);
        const filledCount = Number(ctx.scheduledCount ?? 0);
        const isFilled = slots > 0 && filledCount >= slots;
        const startMs = ctx.StartDateTimeRaw ? new Date(ctx.StartDateTimeRaw).getTime() : NaN;
        const isPast = Number.isFinite(startMs) && startMs < nowMs;
        if (isFilled || isPast) skippedJobIds.push(id);
      });

      // Will any of the changed werkers actually be newly invited?
      const hasInviteChange = changed.some((u) => u.isInvited === true);

      // If every series job is filled/past and the scheduler is trying to add
      // invites, abort before submitting — there is nothing to invite to.
      if (hasInviteChange && skippedJobIds.length === resolvedJobIds.length) {
        await presentAlert({
          header: "No Open Jobs",
          message: "All jobs in this series are filled or in the past. No invites were sent.",
          buttons: ["OK"],
        });
        return;
      }

      // Map job context to expected interface (shared fields from first job)
      const jobContextInfo = {
        company: firstJobContext.Company,
        location: firstJobContext.Location,
        locationCoordinates: firstJobContext.LocationCoordinates,
        pay: firstJobContext.Pay,
        shiftIdentifier: firstJobContext.ShiftIdentifier,
        shiftNotes: firstJobContext.ShiftNotes,
        dateTimeStart: firstJobContext.StartDateTimeRaw,
        dateTimeFinish: firstJobContext.FinishDateTimeRaw,
        numJobSlots: firstJobContext.numJobSlots ?? 1,
        jobStatusSched: firstJobContext.JobStatusSched,
      };

      // Build a per-job dates map so the cloud function stamps each job with its own date
      const jobDatesMap: Record<string, { dateTimeStart: string; dateTimeFinish: string }> = {};
      resolvedJobIds.forEach((id, i) => {
        const ctx = allJobContexts[i];
        if (ctx) {
          jobDatesMap[id] = {
            dateTimeStart: ctx.StartDateTimeRaw,
            dateTimeFinish: ctx.FinishDateTimeRaw,
          };
        }
      });

      // Map changed werkers to WerkerChange format
      const werkerChanges: WerkerChange[] = changed
        .filter((u) => !!u?.WerkerUid && u.WerkerUid !== SUPPORT_MONKEY_UID)
        .map((u) => ({
          werkerUid: u.WerkerUid,
          isInvited: u.isInvited,
          existingDocId: u.DocId || null,
          werkerFirstName: u.WerkerFirstName,
          werkerLastName: u.WerkerLastName,
          werkerProfilePicURL: u.WerkerProfilePicURL || "",
          werkerCompany: u.WerkerCompany || "",
        }));

      // Prepare scheduler profile
      const schedulerProfile = {
        firstName: profile.FirstName,
        lastName: profile.LastName,
        userId: profile.UserId,
        profilePicURL: profile.ProfilePicURL,
      };

      // Call async function
      await fx_FB_WerkerInvitePending(profile.UserId, resolvedJobIds, werkerChanges, jobContextInfo, schedulerProfile, jobDatesMap);

      // Show toast — note any skipped jobs so the scheduler knows not every
      // series job received the invite.
      const skipNotice =
        hasInviteChange && skippedJobIds.length > 0
          ? ` (skipped ${skippedJobIds.length} of ${resolvedJobIds.length} — already filled or past)`
          : "";
      presentToast({
        message: `Inviting ${werkerChanges.length} werker(s) on ${resolvedJobIds.length} job(s)${skipNotice}...`,
        duration: skipNotice ? 3000 : 1500,
        position: "top",
        color: "medium",
      });

      // Navigate
      if (desktopMode?.onComplete) {
        desktopMode.onComplete({ nextView: "details", jobId: resolvedJobIds[0] });
      } else {
        const cameFromDetail = !!(history?.location?.state as any)?.cameFromDetail;
        if (cameFromDetail) {
          // When we came from an edit/clone flow, the history stack is:
          //   [..., SchedShiftDetail(old), SelectWerkersSeries]
          // A replace here would create a duplicate SchedShiftDetail entry,
          // requiring two back-presses. Instead, just goBack() to the
          // existing SchedShiftDetail — its onSnapshot listener will
          // refresh the data automatically.
          history.goBack();
        } else {
          history.replace(`/Home`);
        }
      }
    } catch (e) {
      presentToast({
        message: "Operation failed, please try again.",
        duration: 2500,
        position: "top",
        color: "danger",
      });
    }
  };


  if (isDesktopMode) {
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <div
          style={{
            backgroundColor: "var(--ion-background-color)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: 56,
            padding: "0 12px",
            boxSizing: "border-box",
            borderBottom: "1px solid var(--ion-color-light-shade)",
            flexShrink: 0,
          }}
        >
          <IonButton
            onClick={() => desktopMode?.onComplete({ nextView: "details", jobId: resolvedJobIds[0] || jobIds[0] })}
            fill="clear"
            style={{ margin: 0 }}
          >
            Back
          </IonButton>
          <div style={{ flex: 1, textAlign: "center" }}>
            <strong><h2 style={{ margin: 0, lineHeight: "24px" }}>{HEADERTEXT_TYPES.ChooseWerkers}</h2></strong>
          </div>
          <IonButton
            onClick={handleSubmit}
            fill="clear"
            color="dark"
            style={{ margin: 0 }}
          >
            <strong>Submit Series</strong>
          </IonButton>
          <IonButton
            onClick={() => desktopMode?.onComplete({ nextView: "details", jobId: resolvedJobIds[0] || jobIds?.[0] || "" })}
            fill="clear"
            color="medium"
            style={{ margin: 0 }}
          >
            Invite Later
          </IonButton>
        </div>

        <IonContent
          ref={contentRef}
          scrollEvents={true}
          onIonScroll={onContentScroll}
          style={{ flex: 1 }}
        >
          <IonSearchbar
            placeholder="Filter by #Tag, name, company, etc..."
            value={searchText}
            onIonChange={(e: any) => setSearchText(e.detail.value!)}
          />

          <IonAccordionGroup
            value={crewAccordionValue}
            onIonChange={(e) => setCrewAccordionValue((e.detail.value as string) || undefined)}
          >
            <IonAccordion value="crews">
              <IonItem slot="header" lines="full">
                <IonLabel>
                  <strong>Add Crew</strong>
                </IonLabel>
                <IonBadge slot="end" color="medium">
                  {crews?.length || 0}
                </IonBadge>
              </IonItem>

              <div slot="content">
                {crewsLoading ? (
                  <IonItem lines="none">
                    <IonLabel color="medium">
                      Loading crews...
                    </IonLabel>
                  </IonItem>
                ) : !crews || crews.length === 0 ? (
                  <IonItem lines="none">
                    <IonLabel color="medium">
                      No crews yet.
                    </IonLabel>
                  </IonItem>
                ) : (
                  <IonList>
                    {crews.map((crew: any) => (
                      <IonItem key={crew.id} button={true} detail={false} onClick={() => applyCrewToSelection(crew)}>
                        <IonAvatar slot="start">
                          {crew.avatarUrl ? (
                            <img
                              src={crew.avatarUrl}
                              alt={crew.name}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "14px", background: "#e5e5e5", color: "#333" }}>
                              {getInitials(crew.name)}
                            </div>
                          )}
                        </IonAvatar>

                        <IonLabel className="ion-text-wrap">
                          <strong>{crew.name}</strong>
                          <div style={{ marginTop: "2px" }}>
                            <IonText color="medium">
                              {crew.description && crew.description.trim().length > 0 ? crew.description : "No description"}
                            </IonText>
                          </div>
                        </IonLabel>

                        <IonBadge slot="end" color="medium">
                          {crew.memberCount}
                        </IonBadge>
                      </IonItem>
                    ))}
                  </IonList>
                )}
              </div>
            </IonAccordion>
          </IonAccordionGroup>

          <IonGrid>
            <IonItem button detail={false} onClick={handleSelectAllClick}>
              <IonLabel>
                {searchText?.trim() ? "Select All (filtered)" : "Select All"}
                <IonText color="medium">
                  <div style={{ marginTop: "2px", fontSize: "13px" }}>
                    {visibleSelectedCount} selected / {visibleUidsCount} associates
                  </div>
                </IonText>
              </IonLabel>
              <IonCheckbox slot="end" checked={allFilteredSelected} indeterminate={someFilteredSelected} disabled={true} />
            </IonItem>

            <IonList className="searchBar">
              {filteredSorted.map((val) => (
                <IonItem key={val.WerkerUid} className="searchBar">
                  <IonAvatar slot="start">
                    <img src={val.WerkerProfilePicURL} alt="" />
                  </IonAvatar>
                  <IonLabel>
                    <h2>{val.WerkerFirstName} {val.WerkerLastName}</h2>
                    <p>{val.WerkerCompany}</p>
                  </IonLabel>
                  <IonCheckbox slot="end" checked={val.isInvited} onIonChange={(e) => handleCrewList(val.WerkerUid, e)} />
                </IonItem>
              ))}
            </IonList>
          </IonGrid>
        </IonContent>
      </div>
    );
  }

  return (
    <IonPage>
      <WerkerSchedulerHeader title={HEADERTEXT_TYPES.ChooseWerkers} />

      <IonContent
        ref={contentRef}
        scrollEvents={true}
        onIonScroll={onContentScroll}
      >
        <IonSearchbar
          placeholder="Filter by #Tag, name, company, etc..."
          value={searchText}
          onIonChange={(e: any) => setSearchText(e.detail.value!)}
        />

        {/* Crews Quick Add */}
        <IonAccordionGroup
          value={crewAccordionValue}
          onIonChange={(e) => setCrewAccordionValue((e.detail.value as string) || undefined)}
        >
          <IonAccordion
            value="crews"
          >
            <IonItem
              slot="header"
              lines="full"
            >
              <IonLabel>
                <strong>Add Crew</strong>
              </IonLabel>

              <IonBadge
                slot="end"
                color="medium"
              >
                {crews?.length || 0}
              </IonBadge>
            </IonItem>

            <div slot="content">
              {crewsLoading ? (
                <IonItem
                  lines="none"
                >
                  <IonLabel
                    color="medium"
                  >
                    Loading crews...
                  </IonLabel>
                </IonItem>
              ) : !crews || crews.length === 0 ? (
                <IonItem
                  lines="none"
                >
                  <IonLabel
                    color="medium"
                  >
                    No crews yet.
                  </IonLabel>
                </IonItem>
              ) : (
                <IonList>
                  {crews.map((crew: any) => (
                    <IonItem
                      key={crew.id}
                      button={true}
                      detail={false}
                      onClick={() => applyCrewToSelection(crew)}
                    >
                      <IonAvatar
                        slot="start"
                      >
                        {crew.avatarUrl ? (
                          <img
                            src={crew.avatarUrl}
                            alt={crew.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 800,
                              fontSize: "14px",
                              background: "#e5e5e5",
                              color: "#333",
                            }}
                          >
                            {getInitials(crew.name)}
                          </div>
                        )}
                      </IonAvatar>

                      <IonLabel
                        className="ion-text-wrap"
                      >
                        <strong>{crew.name}</strong>

                        <div style={{ marginTop: "2px" }}>
                          <IonText
                            color="medium"
                          >
                            {crew.description && crew.description.trim().length > 0
                              ? crew.description
                              : "No description"}
                          </IonText>
                        </div>
                      </IonLabel>

                      <IonBadge
                        slot="end"
                        color="medium"
                      >
                        {crew.memberCount}
                      </IonBadge>
                    </IonItem>
                  ))}
                </IonList>
              )}
            </div>
          </IonAccordion>
        </IonAccordionGroup>

        <IonGrid>
          <IonItem
            button
            detail={false}
            onClick={handleSelectAllClick}
          >
            <IonLabel>
              {searchText?.trim() ? "Select All (filtered)" : "Select All"}

              <IonText
                color="medium"
              >
                <div style={{ marginTop: "2px", fontSize: "13px" }}>
                  {visibleSelectedCount} selected / {visibleUidsCount} associates
                </div>
              </IonText>
            </IonLabel>

            {/* Display-only checkbox */}
            <IonCheckbox
              slot="end"
              checked={allFilteredSelected}
              indeterminate={someFilteredSelected}
              disabled={true}
            />
          </IonItem>

          <IonList
            className="searchBar"
          >
            {filteredSorted.map((val) => (
              <IonItem
                key={val.WerkerUid}
                className="searchBar"
              >
                <IonAvatar
                  slot="start"
                >
                  <img src={val.WerkerProfilePicURL} alt="" />
                </IonAvatar>
                <IonLabel>
                  <h2>
                    {val.WerkerFirstName} {val.WerkerLastName}
                  </h2>
                  <p>{val.WerkerCompany}</p>
                </IonLabel>
                <IonCheckbox
                  slot="end"
                  checked={val.isInvited}
                  onIonChange={(e) => handleCrewList(val.WerkerUid, e)}
                />
              </IonItem>
            ))}
          </IonList>
        </IonGrid>
      </IonContent>

      <IonFooter>
        <IonToolbar>
          <IonButton
            expand="block"
            color="secondwarning"
            onClick={handleSubmit}
          >
            Submit Series
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            color="medium"
            onClick={() => {
              if (desktopMode?.onComplete) {
                desktopMode.onComplete({ nextView: "details", jobId: resolvedJobIds[0] || "" });
              } else {
                const anchorId = resolvedJobIds[0];
                if (anchorId) {
                  history.replace(`/SchedShiftDetail/${anchorId}`);
                } else {
                  history.goBack();
                }
              }
            }}
          >
            Invite Later
          </IonButton>
          <br />
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default SelectWerkersSeries;
